// src/components/credits/AddFundsPanel.js
//
// Add Funds (mission 005): ONE panel for Profile → Add Funds, /balance and
// the Add Funds modal. The balance is US cents shown as USD — never "credits".
//
//   Balance   available now; on hold (a payment under review); reserved (open
//             chats and calls); paused. No session → an explicit Sign in.
//   Methods   Crypto — transfer ETH on Base from your wallet (create_order →
//             deposit(bytes32)); Card — coming soon (summary.funding.fiat)
//   Journey   lib/fundingJourney.js: the open deposit survives a reload and is
//             shared by every panel and tab of this account + network. Every
//             result (order status, wallet answer) is bound to the journey's
//             generation and order and dropped if the journey moved on. The
//             submission is an exclusive claim written BEFORE the wallet is
//             asked; if it cannot be saved, nothing is signed. After the wallet
//             was asked, only the order's own outcome settles it: never re-sent,
//             never replaced without consent naming it.
//   Policy    summary.funding says up front whether this account may add funds.
//   Amount    a typed amount (whole cents, within summary.custom: $5–$500) or a
//             pack pill that fills it; Continue orders the pack when the amount
//             is a pack's, else pack 'custom' with amount_cents (decision 11).
//             initialAmountCents prefills it (the welcome offer's card).
//   Promo     summary.promo eligible (mission 008, decision 008-02 §6): the
//             banner "40 % off your first top-up · ends in hh:mm:ss" (a live
//             countdown to promo.expires_at), the offer's cards as pills
//             ("$25 for $15": credits for pay), and under a typed amount the
//             credits it buys with its TRUE discount (§7). The server grants it.
//
// onOrderPhase(phase, { account, networkId, orderId }) reports the CURRENT
// journey's order phase to the Add Funds modal (its onFunded fires only for a
// credited order, and never after the modal closed); 'order_created' when this
// panel created an order.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Loader2, RefreshCw, Wallet } from 'lucide-react';
import { useSiweAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { creditsApi, depositFundingOrder, formatUsd } from '../../lib/credits';
import { canDeposit, FUNDING_POLL_MS, fundingJourneyStore, isUserRejection, weiToEthExact } from '../../lib/fundingJourney';
import { invalidateBalance, useBalanceSummary } from '../../hooks/useBalanceSummary';
import { EMAIL_SESSION_COPY } from '../../hooks/usePaymentGate';
import { trueDiscountPct, welcomeBonusCents, welcomeCards } from '../../lib/addFunds';
import { useCountdown } from '../../hooks/useCountdown';

const KIND = {
  funding_credit: 'Added funds', funding_reversal: 'Correction', hold: 'Reserved', hold_release: 'Released',
  charge: 'Charge', charge_refund: 'Refund', money_out: 'Withdrawal', money_out_reversal: 'Withdrawal reversed',
};
const NOT_OPEN = {
  not_tester: 'Adding funds is open to beta testers for now.',
  free_test_requires_testnet: "Adding funds isn't available right now.",
  funding_mode_off: "Adding funds isn't available right now.",
  funding_disabled: "Adding funds isn't available right now.",
  not_configured: "Adding funds isn't available right now.",
};
const STATE_COPY = {
  deposit_pending: 'Deposit sent. It is added to your balance about 2 Base blocks after it lands (usually under a minute). You can leave this page.',
  review: 'We received your payment and are double-checking it. Support will review it — nothing is lost.',
  credited: 'Funds added to your balance.',
  order_expired: 'This quote expired before it was paid. Pick an amount again for a fresh price.',
};
const HELD_COPY = 'Payment received. Larger amounts are added once Base finalizes them (about 20 minutes). You can leave this page.';
const OPEN = ['awaiting_confirmation', 'submitting', 'deposit_pending', 'wallet_submission_uncertain', 'review'];
const NO_LOCKS = "This browser can't safely coordinate a deposit. Use an up-to-date browser.";
const NOT_SAVED = 'This deposit could not be saved in your browser, so nothing was signed. Allow site storage for Lunexx and try again.';

const safeStorage = () => { try { return window.localStorage; } catch { return null; } };
const newClaimId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

function Pill({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`flex h-9 items-center gap-2 rounded-full px-4 text-[14px] font-medium transition-colors ${active
        ? 'border border-white/[0.08] bg-[#1E1E1E] text-white shadow-sm' : 'border border-transparent text-zinc-500 hover:text-zinc-300'}`}>
      {children}
    </button>
  );
}
const PRIMARY = 'flex h-10 items-center justify-center gap-2 rounded-[10px] bg-white px-4 text-[14px] font-semibold text-black hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-50';
const SECONDARY = 'h-9 rounded-full border border-white/[0.08] bg-[#181818] px-4 text-[13px] text-white shadow-inner hover:bg-[#1D1D1D]';
const TEXT_BUTTON = 'h-9 px-4 text-[14px]';
const CUSTOM_BOUNDS = { min_cents: 500, max_cents: 50000 };
const AMOUNT_INPUT = /^\d*(\.\d{0,2})?$/;
/** "12" / "12.5" / "12.50" → 1250 cents; anything else → null */
const toCents = (v) => {
  if (!v || !/^\d+(\.\d{0,2})?$/.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};
/** bounds read as "$5" / "$250.50" */
const usdShort = (cents) => (cents % 100 ? formatUsd(cents) : `$${cents / 100}`);

export default function AddFundsPanel({ returnTo = null, initialMethod = 'crypto', onOrderPhase = null, compact = false, initialAmountCents = null }) {
  const { ensureAuth, sessionWallet } = useSiweAuth();
  const { walletAddress, authMethod, canSign } = useWallet();
  // an email session cannot sign a deposit (mission 007): no order controls for it
  const emailOnly = authMethod === 'email' && canSign !== true;
  const { summary, refresh } = useBalanceSummary();
  const [method, setMethod] = useState(initialMethod === 'card' ? 'card' : 'crypto');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [held, setHeld] = useState(false);          // credited only at finality
  const [replacing, setReplacing] = useState(null); // { pack, generation }: consent names the journey it replaces
  const [journey, setJourney] = useState(null);
  const [amount, setAmount] = useState(() => (initialAmountCents > 0 ? (initialAmountCents / 100).toFixed(2) : ''));   // the Amount field, as typed
  const myClaim = useRef(null);                     // the claim THIS panel is waiting on the wallet for
  const mounted = useRef(true);
  const onPhaseRef = useRef(onOrderPhase);
  onPhaseRef.current = onOrderPhase;
  // the poll must not restart when the auth helper's identity changes
  const authRef = useRef(ensureAuth);
  authRef.current = ensureAuth;
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => { refresh(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- a fresh read on open

  const networkId = summary?.network_id ?? null;
  const store = useMemo(() => fundingJourneyStore(safeStorage(), walletAddress, networkId), [walletAddress, networkId]);
  useEffect(() => {
    setJourney(store.get());
    return store.subscribe((j) => { if (mounted.current) setJourney(j); });   // another panel or tab moved it
  }, [store]);
  const sync = useCallback(() => { if (mounted.current) setJourney(store.get()); }, [store]);

  // the order's own progress decides the journey (resumed after a reload too);
  // every answer is applied to the generation + order it was asked for
  const generation = journey?.generation ?? null;
  const orderId = journey?.order?.order_id ?? null;
  const watching = !!orderId && OPEN.includes(journey?.state);
  const checkOrder = useCallback(async (gen, oid) => {
    try {
      const token = await authRef.current();
      const s = await creditsApi(token, { action: 'order_status', order_id: oid });
      const next = await store.applyOrderPhase(gen, oid, s.phase);
      if (!next) return null;                                  // a late answer for a replaced journey: dropped
      if (mounted.current) setHeld(s.phase === 'held_until_finality');
      onPhaseRef.current?.(s.phase, { account: walletAddress, networkId, orderId: oid });
      if (s.phase === 'credited') invalidateBalance(walletAddress);
      sync();
      return next;
    } catch { return null; }
  }, [store, walletAddress, networkId, sync]);
  useEffect(() => {
    if (!watching) return undefined;
    let n = 0, t = null, stop = false;
    const gen = generation, oid = orderId;
    const tick = async () => {
      n += 1;
      const next = await checkOrder(gen, oid);
      if (stop || (next && !OPEN.includes(next.state))) return;
      t = window.setTimeout(tick, n < 40 ? FUNDING_POLL_MS : 30_000);
    };
    tick();
    return () => { stop = true; window.clearTimeout(t); };
  }, [watching, generation, orderId, checkOrder]);

  const pickPack = async (pack, { replaces = null } = {}) => {
    setError(null);
    const cur = store.get();
    if (cur && OPEN.includes(cur.state) && cur.generation !== replaces) { setReplacing({ pack, generation: cur.generation }); return; }
    setReplacing(null);
    setBusy(true);
    try {
      const token = await ensureAuth();
      const order = await creditsApi(token, pack.id === 'custom'
        ? { action: 'create_order', pack: 'custom', amount_cents: pack.amount_cents, asset: 'ETH' }
        : { action: 'create_order', pack: pack.id, asset: 'ETH' });
      await store.start({ pack, order }, { replaces });
      onPhaseRef.current?.('order_created', { account: walletAddress, networkId, orderId: order?.order_id ?? null });
      sync();
    } catch (e) {
      if (!mounted.current) return;
      if (e?.code === 'journey_active') setError('Another deposit was started meanwhile. Finish or replace it first.');
      else if (e?.code === 'locks_unavailable') setError(NO_LOCKS);
      else if (e?.code === 'storage_unavailable') setError(NOT_SAVED);
      else setError(e.message);
      sync();
    } finally { if (mounted.current) setBusy(false); }
  };

  /** the click carries what was RENDERED; the lock claims exactly that journey (decision 04 §2) */
  const deposit = async (shown) => {
    setError(null); setBusy(true);
    const claimId = newClaimId();
    const c = await store.claim(shown, claimId);
    if (!c.ok) {
      if (mounted.current) {
        setError(c.code === 'storage_unavailable' ? NOT_SAVED
          : c.code === 'locks_unavailable' ? NO_LOCKS
          : c.code === 'mismatch' ? 'This deposit changed in another tab. Check the amount below and tap Deposit again.' : null);
        setBusy(false);
      }
      sync();
      return;
    }
    const j = c.journey;
    myClaim.current = claimId;
    sync();
    try {
      const txHash = await depositFundingOrder(j.order, sessionWallet || walletAddress);
      await store.settleSubmission(j.generation, claimId, { kind: 'sent', txHash });
    } catch (e) {
      if (e?.beforeSigning) {
        await store.settleSubmission(j.generation, claimId, { kind: 'rejected' });   // nothing was signed: the claim is released
        if (mounted.current) setError(e.message);
      } else if (isUserRejection(e)) {
        await store.settleSubmission(j.generation, claimId, { kind: 'rejected' });
        if (mounted.current) setError('Deposit cancelled in your wallet. Nothing was sent.');
      } else {
        await store.settleSubmission(j.generation, claimId, { kind: 'send_uncertain' });   // it may have broadcast: never resend by itself
      }
    } finally {
      myClaim.current = null;
      if (mounted.current) setBusy(false);
      sync();
    }
  };

  const signIn = async () => {
    setError(null);
    try { await ensureAuth(); await refresh(); } catch (e) { if (mounted.current) setError(e?.message || 'Sign-in did not complete.'); }
  };

  const funding = summary?.funding ?? (summary ? { enabled: !!summary.funding_enabled, reason: summary.funding_enabled ? null : 'funding_disabled' } : null);
  const fiat = funding?.fiat ?? { enabled: false, reason: 'no_provider' };
  const state = journey?.state;
  const open = journey && OPEN.includes(state);
  const mine = state === 'submitting' && journey?.claim?.id && journey.claim.id === myClaim.current;
  const bounds = summary?.funding?.custom ?? summary?.custom ?? CUSTOM_BOUNDS;
  const cents = toCents(amount);
  const outOfBounds = cents !== null && (cents < bounds.min_cents || cents > bounds.max_cents);
  const packs = summary?.packs || [];
  const matching = cents === null ? null : packs.find((p) => p.amount_cents === cents) ?? null;
  const ordering = busy || (open && state !== 'awaiting_confirmation');
  const promo = summary?.promo?.eligible === true ? summary.promo : null;
  const cards = welcomeCards(promo);
  const headlinePct = (cards.find((c) => c.best) ?? cards[cards.length - 1])?.discount_pct ?? 0;
  const clock = useCountdown(promo ? promo.expires_at : null);
  const offerLive = !!promo && cards.length > 0 && !clock.done;
  const typedBonus = offerLive && cents !== null && !outOfBounds ? welcomeBonusCents(promo, cents) : 0;

  return (
    <div className="flex min-w-0 flex-col gap-5 text-white">
      {/* balance */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-zinc-500">Your balance</div>
          <div className="font-mono text-3xl font-semibold tabular-nums">{summary ? formatUsd(summary.available_cents) : '—'}</div>
          {summary?.held_cents > 0 && (
            <div className="mt-1 text-[15px] text-amber-300/80">{formatUsd(summary.held_cents)} on hold while a payment is reviewed</div>
          )}
          {summary?.reserved_cents > 0 && (
            <div className="mt-1 text-[15px] leading-relaxed text-zinc-400">
              {formatUsd(summary.reserved_cents)} reserved for open chats and calls — unused funds come back when they end
            </div>
          )}
          {summary?.frozen && <div className="mt-1 text-[15px] text-red-300/80">Payments are paused for a moment.</div>}
        </div>
        <button type="button" onClick={() => refresh()} aria-label="Refresh balance"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-white/[0.05] hover:text-white">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* method */}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Payment method">
        <Pill active={method === 'crypto'} onClick={() => setMethod('crypto')}><Wallet size={14} /> Crypto · ETH on Base</Pill>
        <Pill active={method === 'card'} onClick={() => setMethod('card')}><CreditCard size={14} /> Card</Pill>
      </div>

      {method === 'card' && (
        <div className="rounded-[18px] border border-white/[0.06] bg-[#181818] p-4 text-[15px] leading-relaxed text-zinc-400">
          {fiat.enabled ? 'Card payments are available.' : 'Card payments are coming. Until then, add funds with ETH from your wallet.'}
        </div>
      )}

      {method === 'crypto' && (
        <div className="flex flex-col gap-3">
          {!emailOnly && <p className="text-[15px] leading-relaxed text-zinc-400">Transfer ETH on Base from your wallet. Pick an amount; you confirm the exact ETH before anything is sent.</p>}
          {!summary ? (
            !sessionWallet ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-[15px] text-zinc-400">Sign in to see your balance and add funds.</p>
                <button type="button" onClick={signIn} className={PRIMARY}>Sign in</button>
              </div>
            ) : (
              <p className="text-[15px] text-zinc-400">Balance unavailable right now. Refresh in a moment.</p>
            )
          ) : emailOnly ? (
            <p className="text-[15px] leading-relaxed text-zinc-400" role="status">{EMAIL_SESSION_COPY}</p>
          ) : !store.locked ? (
            <p className="text-[15px] text-zinc-400" role="status">{NO_LOCKS}</p>
          ) : !funding?.enabled ? (
            <p className="text-[15px] text-zinc-400">{NOT_OPEN[funding?.reason] ?? "Adding funds isn't available right now."}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {offerLive && (
                <p className={`font-mono text-[13px] tabular-nums ${clock.urgent ? 'text-amber-300' : 'text-zinc-300'}`} role="note">
                  {`${headlinePct} % off your first top-up · ends in ${clock.text}`}
                </p>
              )}
              <label htmlFor="add-funds-amount" className="text-[13px] font-medium text-zinc-500">Amount</label>
              <div className="flex h-10 items-center rounded-[10px] border border-white/[0.08] bg-[#181818] px-3 focus-within:border-white/20">
                <span className="font-mono text-[15px] text-zinc-500" aria-hidden="true">$</span>
                <input id="add-funds-amount" type="text" inputMode="decimal" autoComplete="off" placeholder="0.00"
                  value={amount} disabled={ordering}
                  onChange={(e) => { const v = e.target.value.trim(); if (AMOUNT_INPUT.test(v)) setAmount(v); }}
                  className="min-w-0 flex-1 bg-transparent pl-1 font-mono text-[15px] tabular-nums text-white outline-none placeholder:text-zinc-600 disabled:opacity-50" />
              </div>
              <div className="flex flex-wrap gap-2">
                {offerLive ? cards.map((c) => (
                  <button key={c.pay_cents} type="button" disabled={ordering} aria-pressed={cents === c.pay_cents}
                    onClick={() => setAmount((c.pay_cents / 100).toFixed(2))}
                    className={`h-9 rounded-full border px-4 font-mono text-[14px] tabular-nums text-white shadow-inner disabled:opacity-40 ${cents === c.pay_cents
                      ? 'border-white/30 bg-[#1E1E1E]' : 'border-white/[0.08] bg-[#181818] hover:bg-[#1D1D1D]'}`}>
                    {`${usdShort(c.credits_cents)} for ${usdShort(c.pay_cents)}`}
                  </button>
                )) : packs.map((p) => (
                  <button key={p.id} type="button" disabled={ordering} aria-pressed={matching?.id === p.id}
                    onClick={() => setAmount((p.amount_cents / 100).toFixed(2))}
                    className={`h-9 rounded-full border px-4 font-mono text-[14px] tabular-nums text-white shadow-inner disabled:opacity-40 ${matching?.id === p.id
                      ? 'border-white/30 bg-[#1E1E1E]' : 'border-white/[0.08] bg-[#181818] hover:bg-[#1D1D1D]'}`}>
                    {formatUsd(p.amount_cents)}
                  </button>
                ))}
              </div>
              {typedBonus > 0 && (
                <p className="text-[13px] text-zinc-300" role="status">
                  {`You'll get ${formatUsd(cents + typedBonus)} of credits · ${trueDiscountPct(promo, cents)} % off`}
                </p>
              )}
              <p className={`text-[13px] ${outOfBounds ? 'text-amber-300/80' : 'text-zinc-500'}`}>
                Any amount from {usdShort(bounds.min_cents)} to {usdShort(bounds.max_cents)}
              </p>
              <button type="button" disabled={ordering || cents === null || cents <= 0 || outOfBounds}
                onClick={() => pickPack(matching ?? { id: 'custom', amount_cents: cents })} className={`${PRIMARY} self-start`}>
                {busy && !open && <Loader2 size={14} className="animate-spin" />} Continue
              </button>
            </div>
          )}

          {replacing && (
            <div className="flex flex-col gap-2 rounded-[18px] border border-white/[0.06] bg-[#181818] p-4 text-[15px] text-zinc-300">
              You already have an open deposit{journey?.pack ? ` for ${formatUsd(journey.pack.amount_cents)}` : ''}{journey?.order?.order_id ? ` (order ${journey.order.order_id})` : ''}. Replace it with a new one for {formatUsd(replacing.pack.amount_cents)}?
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => pickPack(replacing.pack, { replaces: replacing.generation })} className={SECONDARY}>Replace that deposit</button>
                <button type="button" onClick={() => setReplacing(null)} className={`${TEXT_BUTTON} text-zinc-500 hover:text-white`}>Keep the open one</button>
              </div>
            </div>
          )}

          {(state === 'awaiting_confirmation' || state === 'submitting') && journey.order && !emailOnly && (
            <div className="flex flex-col gap-3 rounded-[18px] border border-white/[0.06] bg-[#181818] p-4">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px] font-mono text-[14px] tabular-nums">
                  <tbody className="[&_td]:py-1.5">
                    <tr><td className="pr-4 text-zinc-500">You send</td><td className="text-right text-white">{weiToEthExact(journey.order.call?.value_wei) ?? '—'} ETH</td></tr>
                    <tr><td className="pr-4 text-zinc-500">Added to your balance</td><td className="text-right text-white">{formatUsd(journey.order.amount_cents)}</td></tr>
                    <tr><td className="pr-4 text-zinc-500">Quote valid until</td><td className="text-right text-zinc-300">{timeOf(journey.order.expires_at)}</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[15px] text-zinc-500">Plus the Base network fee, paid from your wallet.</p>
              {state === 'awaiting_confirmation' && !journey.claim && !store.locked ? (
                // a restored confirmation in a browser without Web Locks: nothing can be claimed or
                // cancelled safely, so no action is offered (and the journey is left as it is)
                <p className="text-[15px] text-zinc-400" role="status">{NO_LOCKS}</p>
              ) : state === 'awaiting_confirmation' && !journey.claim ? (
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={busy} className={PRIMARY}
                    onClick={() => deposit({ generation: journey.generation, orderId: journey.order.order_id, valueWei: journey.order.call?.value_wei, packId: journey.pack?.id })}>
                    {busy && <Loader2 size={14} className="animate-spin" />} Deposit
                  </button>
                  <button type="button" onClick={async () => { await store.cancel(journey.generation); sync(); }} disabled={busy}
                    className={`${TEXT_BUTTON} text-zinc-500 hover:text-white`}>Cancel</button>
                </div>
              ) : mine ? (
                <p className="flex items-center gap-2 text-[15px] text-zinc-300" role="status"><Loader2 size={14} className="animate-spin" /> Confirm the deposit in your wallet.</p>
              ) : (
                <p className="text-[15px] leading-relaxed text-amber-100/80" role="status">
                  This deposit may already have been sent from this browser. We're checking the order — don't send it again.
                </p>
              )}
            </div>
          )}

          {state === 'wallet_submission_uncertain' && (
            <div className="flex flex-col gap-2 rounded-[18px] border border-amber-400/20 bg-amber-400/[0.05] p-4 text-[15px] text-amber-100/90">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle size={15} /> We couldn't confirm whether your wallet sent this deposit.</div>
              <p className="leading-relaxed text-amber-100/70">Don't send it again. We keep checking the order; if it arrived it is added to your balance. If nothing arrives, contact support with order {journey.order?.order_id}.</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => checkOrder(journey.generation, journey.order?.order_id)} className={SECONDARY}>Check status</button>
                <button type="button" onClick={() => journey.pack && setReplacing({ pack: journey.pack, generation: journey.generation })}
                  className={`${TEXT_BUTTON} text-zinc-400 hover:text-white`}>I'm sure nothing was sent — start a new deposit</button>
              </div>
            </div>
          )}

          {state && STATE_COPY[state] && (
            <div className="rounded-[18px] bg-white/[0.04] p-3 text-[15px] leading-relaxed text-zinc-300" role="status">
              {state === 'deposit_pending' && held ? HELD_COPY : STATE_COPY[state]}
            </div>
          )}
        </div>
      )}

      {returnTo && (
        <Link to={returnTo} className={`${PRIMARY} self-start`}>Back to where you were</Link>
      )}
      {error && <div className="text-[15px] text-red-300/80" role="alert">{error}</div>}

      {!compact && (summary?.ledger || []).length > 0 && (
        <div>
          <div className="mb-1 text-[13px] font-medium text-zinc-500">Recent activity</div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[280px] font-mono text-[14px] tabular-nums">
              <tbody>
                {summary.ledger.map((l) => (
                  <tr key={l.id} className="border-b border-white/[0.04] last:border-0">
                    <td className="py-2 pr-4 text-zinc-400">{KIND[l.kind] ?? l.kind}</td>
                    <td className={`py-2 text-right ${l.amount < 0 ? 'text-zinc-500' : 'text-[#A3E635]'}`}>{formatUsd(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
